import {
  ExtendedAxiosError,
  useBadRequestOrConflictError,
} from '@issuemanager/apps/client/hooks';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MinimalUserDto, SaveAttachmentDto } from '@issuemanager/api-client';
import {
  ControlledFileUploader,
  ControlledTextField,
} from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  formHasErrorsToDisplay,
  getFormStateErrors,
  joinFormErrorsWithServiceErrors,
  mapFormLabelsToFieldLabel,
} from '@issuemanager/apps/client/utils';
import { useAddIssueComment } from '@issuemanager/client/features/issues';
import {
  ISSUE_COMMENT_LENGTH_MAX,
  IssuePermissionsEnum,
} from '@issuemanager/constants';
import { useSnackbar } from 'notistack';

export type IssueCommentFormValues = {
  comment: string;
  attachments: (SaveAttachmentDto & {
    addedBy: MinimalUserDto;
    createdAt: string;
  })[];
};

export const useIssueCommentForm = (issueCode: string) => {
  const { t } = useTranslation(['common', 'issue']);
  const addComment = useAddIssueComment(issueCode);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isLoading: isUploadAttachmentsLoading } = useAttachmentsContext();

  const form = useForm<IssueCommentFormValues>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      comment: '',
      attachments: [],
    },
  });

  const {
    errors: serviceErrors,
    setErrors: setServiceErrors,
    cleanErrors: cleanServiceErrors,
  } = useBadRequestOrConflictError();

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await addComment.mutateAsync({
        message: values.comment,
        attachments: values.attachments,
      });

      form.reset();
      closeSnackbar();
      enqueueSnackbar(t('issue:comment.success'), {
        variant: 'success',
      });
    } catch (error) {
      const err = error as ExtendedAxiosError;

      setServiceErrors(err);
    }
  });

  const labels = {
    comment: t('issue:comment.labels.message'),
  };

  const rules = {
    comment: {
      required: t('issue:comment.errors.message.required'),
      maxLength: {
        value: ISSUE_COMMENT_LENGTH_MAX,
        message: t('issue:comment.errors.message.maxLength'),
      },
    },
  };

  const attachments = form.watch('attachments');

  const fields = {
    comment: (
      <ControlledTextField
        name='comment'
        placeholder={labels.comment}
        control={form.control}
        rules={rules.comment}
        data-testid='comment-field'
        maxLength={ISSUE_COMMENT_LENGTH_MAX}
      />
    ),
    attachments: (
      <ControlledFileUploader
        control={form.control}
        name='attachments'
        fullWidth
        initialFileList={attachments}
        permissions={[
          IssuePermissionsEnum.IsIssueTypeManager,
          IssuePermissionsEnum.IsOwner,
          IssuePermissionsEnum.IsApprover,
          IssuePermissionsEnum.IsActionOwner,
          IssuePermissionsEnum.IsActionApprover,
          IssuePermissionsEnum.IsCreator,
          IssuePermissionsEnum.IsWatcher,
        ]}
      />
    ),
  };

  const formStateErrors = getFormStateErrors(
    mapFormLabelsToFieldLabel(labels),
    rules,
    form.formState.errors,
  );
  const errors = joinFormErrorsWithServiceErrors(
    formStateErrors,
    serviceErrors,
  );
  const hasErrors = formHasErrorsToDisplay(errors);

  return {
    form,
    commentFields: fields,
    commentSubmit: handleSubmit,
    isLoading: addComment.isPending || isUploadAttachmentsLoading,
    commentErrors: errors,
    commentCleanErrors: cleanServiceErrors,
    hasErrors,
  };
};
